<template>
  <div class="task-kanban-card-view-settings-dropdown">
    <TirPmOptionList :width="280" @checkOpenOptions="emits('checkOpenOptionsViewSettings', $event)">
      <template #optionListTarget="{ isOpened }">
        <div class="task-kanban-card-view-settings-dropdown__target">
          <Cog8ToothIcon class="task-kanban-card-view-settings-dropdown__target-icon" />
          <span class="task-kanban-card-view-settings-dropdown__target-text"
            >Настройка карточек</span
          >
          <ChevronDownIcon
            :class="[
              'task-kanban-card-view-settings-dropdown__target-chevron',
              { 'is-opened': isOpened },
            ]"
          />
        </div>
      </template>
      <template #content>
        <TirPmScrollbar class="scrollbar" :show-on-hover="true">
          <div class="task-kanban-card-view-settings-dropdown__option-list">
            <div v-for="option in optionsSettings" :key="option.ElementType">
              <TirCheckbox
                :class="[
                  'task-kanban-card-view-settings-dropdown__option',
                  { 'is-disabled': disabledCheckboxes.includes(option.ElementType) },
                ]"
                :is-disabled="disabledCheckboxes.includes(option.ElementType)"
                :model-value="checkActive(option.ElementType)"
                @update:modelValue="toggleOption(option.ElementType, $event)"
              >
                <template #mainSign>
                  {{ option.Text }}
                </template>
              </TirCheckbox>
            </div>
          </div>
        </TirPmScrollbar>
      </template>
    </TirPmOptionList>
  </div>
</template>

<script lang="ts" setup>
import { TirCheckbox, TirPmOptionList } from 'tir-components';
import { ChevronDownIcon, Cog8ToothIcon } from 'tir-style-system/icons/outline';
import { computed, PropType } from 'vue';
import { TaskViewElementEnum } from '../enums';
import { TirPmScrollbar } from 'tir-components';

const emits = defineEmits<{
  (e: "update:modelValue", value: Array<TaskViewElementEnum>): void;

  (e: 'checkOpenOptionsViewSettings', value: boolean);
}>();

const props = defineProps({
  modelValue: {
    type: Array as PropType<Array<TaskViewElementEnum>>,
    required: true,
  },
  /**Заблокиованные чекбоксы */
  disabledCheckboxes: {
    type: Array as PropType<Array<TaskViewElementEnum>>,
    default: [],
  },
  /**Скрытые чекбоксы */
  hiddenCheckboxes: {
    type: Array as PropType<Array<TaskViewElementEnum>>,
    default: [],
  },
  /**Отображение сортировки и группировки */
  isSortingAndGroupingHidden: { type: Boolean as PropType<boolean> },
  /**Скрыть статус для настройки таблицы */
  hideStatus: { type: Boolean, default: false },
});

const options = [
  {
    ElementType: TaskViewElementEnum.Author,
    Text: 'Автор',
  },
  {
    ElementType: TaskViewElementEnum.Queue,
    Text: 'Очередь',
  },
  {
    ElementType: TaskViewElementEnum.Performers,
    Text: 'Исполнители',
  },
  {
    ElementType: TaskViewElementEnum.Observers,
    Text: 'Наблюдатели',
  },
  {
    ElementType: TaskViewElementEnum.Status,
    Text: 'Статус',
  },
  {
    ElementType: TaskViewElementEnum.Priority,
    Text: 'Приоритет',
  },
  {
    ElementType: TaskViewElementEnum.Tags,
    Text: 'Теги',
  },
  {
    ElementType: TaskViewElementEnum.Type,
    Text: 'Тип задачи',
  },
  {
    ElementType: TaskViewElementEnum.DateCreate,
    Text: 'Дата создания',
  },
  {
    ElementType: TaskViewElementEnum.EstimateTime,
    Text: 'Оценка времени',
  },
  {
    ElementType: TaskViewElementEnum.WorkTime,
    Text: 'Время работы',
  },
  {
    ElementType: TaskViewElementEnum.Difficulty,
    Text: 'Сложность',
  },
  {
    ElementType: TaskViewElementEnum.Deadline,
    Text: 'Дедлайн',
  },
];

let optionsTasksOverview = [
  {
    ElementType: TaskViewElementEnum.Status,
    Text: 'Статус',
  },
  {
    ElementType: TaskViewElementEnum.Priority,
    Text: 'Приоритет',
  },
  {
    ElementType: TaskViewElementEnum.EstimateTime,
    Text: 't (оценка)',
  },
  {
    ElementType: TaskViewElementEnum.WorkTime,
    Text: 't (затрачено)',
  },
  {
    ElementType: TaskViewElementEnum.Difficulty,
    Text: 'Сложность',
  },
];

/**Отфильтрованный список настроек карточки */
const filteredOptionsTasksOverview = computed(() => {
  if (props.hideStatus) {
    return optionsTasksOverview.filter(
      (option) => option.ElementType !== TaskViewElementEnum.Status,
    );
  } else {
    return optionsTasksOverview;
  }
});
/**Опции для настройки */
const optionsSettings = computed(() =>
  (props.isSortingAndGroupingHidden ? filteredOptionsTasksOverview.value : options).filter(
    (option) => !props.hiddenCheckboxes.includes(option.ElementType),
  ),
);

/**Проверка активности чекбокса */
const checkActive = (type: TaskViewElementEnum) => {
  return props.modelValue.includes(type);
};
/**
 * * Выбрать опцию
 * @param elementType
 */
const toggleOption = (elementType: TaskViewElementEnum) => {
  if (props.modelValue.includes(elementType))
    emits(
      "update:modelValue",
      props.modelValue.filter((_elementType) => _elementType !== elementType),
    );
  else emits("update:modelValue", [...props.modelValue, elementType]);
};

</script>

<style lang="scss" scoped>
.scrollbar {
  max-height: 14rem;
}
.task-kanban-card-view-settings-dropdown {
  border-radius: var(--8);
  // background: var(--neutral-translucent-10, #272d371a);
  &:hover {
    background-color: var(--neutral-translucent-5);
  }
  &__target {
    display: flex;
    align-items: center;
    column-gap: 0.375rem;
    color: var(--text-primary);
    cursor: pointer;
    user-select: none;
    padding: 0.375rem 0.75rem;
  }
  &__target-text {
    font-size: 0.875rem;
    font-weight: 500;
  }
  &__target-icon {
    width: 1rem;
    height: 1rem;
  }
  &__target-chevron {
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s;

    &.is-opened {
      transform: rotate(180deg);
    }
  }
  &__option-list {
    display: flex;
    flex-direction: column;
  }
  &__option {
    padding: 0.75rem;
    cursor: pointer;

    &.is-disabled {
      cursor: default;
    }

    &:not(.is-disabled) {
      &:hover {
        background-color: var(--neutral-translucent-5);
      }
      &:active {
        background-color: var(--neutral-translucent-10);
      }
    }
  }
  &__option-icon {
    width: 1.5rem;
    height: 1.5rem;
  }
  &__option-text {
    font-size: 1rem;
    line-height: 1.5rem;
  }
}
</style>
